import {
  setTauriDictationInputDevicePreference,
  tauriDictationInputDevicePreferenceAtom,
} from '@/atoms/persistedPreferencesAtoms';
import { useIsDictationEnabled } from '@/composition/hosts/dictation/hooks/useIsDictationEnabled';
import {
  DICTATION_INPUT_DEVICE_PREFERENCE_CHANGED_EVENT,
  DICTATION_INPUT_DEVICE_PREFERENCE_REQUEST_EVENT,
  emitDictationInputDevicePreferenceChanged,
  resolveDictationInputDeviceName,
  type DictationInputDevicePreferenceChangedPayload,
} from '@/composition/hosts/dictation/microphone-preference';
import {
  useIsDictateBrowserFieldDetectionEnabled,
  useIsDictateHardwareMicEnabled,
  useIsDictateRemoteDesktopKeystrokeEnabled,
  useIsDictateSeparateMicrophoneEnabled,
  useIsDictateWidgetCursorDisplayEnabled,
} from '@/features/flags';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { isTauri } from '@/tauri/utils';
import { setWidgetCursorDisplayPositioning } from '@/tauri/widget/api';
import { useMainWindowDictationNavigationListener } from '@/tauri/widget/use-main-window-dictation-navigation-listener';
import { logger } from '@/utils/logger/logger';
import { managedListen, type ManagedUnlistenFn } from '@heidi/plugin-core';
import { sanitizeError } from '@heidi/runtime/logger';
import { useAtomValue } from 'jotai';
import posthog from 'posthog-js';
import { useCallback, useEffect, type ReactNode } from 'react';
// eslint-disable-next-line @heidi/rules/no-new-rematch-coupling -- bridges the existing session microphone slice into native Dictate configuration
import { useDispatch, useSelector, useStore } from 'react-redux';
import { type AudioDevice } from 'src-tauri/src/bindings/AudioDevice';
import {
  setDictationBrowserFieldDetectionEnabled,
  setDictationEnabled,
  setDictationHardwareMicEnabled,
  setDictationInputDevice,
  setDictationInputDeviceFallback,
  setDictationRemoteDesktopKeystrokeEnabled,
} from './api';
import { useAutoSelectDictaphoneMic } from './useAutoSelectDictaphoneMic';
import { useHardwareMicDevicesSync } from './useHardwareMicDevicesSync';

/**
 * Polling interval for feature flag refresh (5 minutes).
 *
 * Was 30s. Desktop sessions average ~27 minutes open, so that cadence cost
 * ~53 billable `/flags` requests per session — roughly 16M requests a week
 * across ~307k weekly desktop sessions, entirely from polling. posthog-js
 * already refreshes flags on its own `remote_config_refresh_interval_ms`
 * (5 min by default), so the old cadence ran 10x hotter than the built-in one
 * for no benefit.
 *
 * Deliberately NOT gated on `document.visibilityState`, even though posthog-js
 * gates its own refresh that way. Dictation records in Rust via the global
 * hotkey specifically so it keeps working while the WebView is suspended (see
 * the module docstring in ./api), so a hidden window is exactly when this
 * kill-switch must still be able to reach Rust. Gating on visibility would
 * stall a flip until the window came back to the foreground.
 */
const FF_POLL_INTERVAL_MS = 5 * 60_000;

interface DictationProviderProps {
  children: ReactNode;
}

interface DictationDispatch {
  <T>(action: T): T;
  scribeRecordModel: {
    setTauriInputDevice: (device: AudioDevice) => void;
    getTauriDevices: (params: { updateSelectedDevices: false }) => Promise<unknown>;
  };
}

/**
 * Provider that syncs desktop dictation FF to Rust with automatic polling.
 *
 * Uses `useFeatureFlagEnabled` for reactive flag updates and periodically
 * calls `posthog.reloadFeatureFlags()` to fetch latest values from server.
 * When the flag value changes, it automatically syncs to Rust.
 *
 * Also owns SCR-6773 Dictate mic preference → native device sync and the
 * main-window listener for widget preference broadcasts.
 *
 * On non-Tauri (web), this is a no-op passthrough.
 */
export function DictationProvider({ children }: DictationProviderProps): ReactNode {
  const dispatch = useDispatch<DictationDispatch>();
  const isUserDictationEnabled = useIsDictationEnabled();
  const { isAuthenticated } = useIsAuthenticated();
  // GET_USER can stay cached during logout. Gate Rust with live auth state so
  // its in-memory JWT cannot keep the global hotkey active after sign-out.
  const enabled = isUserDictationEnabled && isAuthenticated;
  const remoteDesktopKeystrokeEnabled = useIsDictateRemoteDesktopKeystrokeEnabled();
  const hardwareMicEnabled = useIsDictateHardwareMicEnabled();
  const widgetCursorDisplayEnabled = useIsDictateWidgetCursorDisplayEnabled();
  const browserFieldDetectionEnabled = useIsDictateBrowserFieldDetectionEnabled();
  const separateMicEnabled = useIsDictateSeparateMicrophoneEnabled();
  const dictationPreference = useAtomValue(tauriDictationInputDevicePreferenceAtom);
  // Event-time store access for the device list — see the CHANGED listener
  // below for why the list is deliberately not subscribed here.
  const store = useStore<{
    scribeRecordModel: { tauriInputDevices?: AudioDevice[] };
  }>();
  const sessionDeviceName = useSelector(
    (state: { scribeRecordModel: { tauriInputDeviceSelected?: { name: string } } }) =>
      state.scribeRecordModel.tauriInputDeviceSelected?.name
  );

  // Main-window listener: the widget asks us to open the sign-in step when the
  // anonymous dictation quota is exhausted. No-op outside the main window / web.
  useMainWindowDictationNavigationListener();

  // Poll for feature flag updates
  useEffect(() => {
    if (!isTauri()) return;

    posthog.reloadFeatureFlags();

    const intervalId = setInterval(() => {
      posthog.reloadFeatureFlags();
    }, FF_POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Sync to Rust when flag value changes
  useEffect(() => {
    if (!isTauri()) return;

    logger.debug({
      event_type: 'DICTATION_FF_SYNC_START',
      details: { enabled },
    });

    setDictationEnabled(enabled).catch((err: unknown) => {
      logger.error({
        event_type: 'DICTATION_FF_SYNC_FAILED',
        details: { error: err },
      });
    });
  }, [enabled]);

  // Sync the remote-desktop keystroke kill-switch (SCR-4586) to Rust.
  useEffect(() => {
    if (!isTauri()) return;

    setDictationRemoteDesktopKeystrokeEnabled(remoteDesktopKeystrokeEnabled).catch(
      (err: unknown) => {
        logger.error({
          event_type: 'DICTATION_REMOTE_DESKTOP_KEYSTROKE_SYNC_FAILED',
          details: { error: err },
        });
      }
    );
  }, [remoteDesktopKeystrokeEnabled]);

  // Sync the hardware-mic HID reader kill switch to Rust.
  useEffect(() => {
    if (!isTauri()) return;

    setDictationHardwareMicEnabled(hardwareMicEnabled).catch((err: unknown) => {
      logger.error({
        event_type: 'DICTATION_HARDWARE_MIC_SYNC_FAILED',
        details: { error: err },
      });
    });
  }, [hardwareMicEnabled]);

  useHardwareMicDevicesSync(hardwareMicEnabled);

  const refreshInputDevices = useCallback(async () => {
    await dispatch.scribeRecordModel.getTauriDevices({ updateSelectedDevices: false });
    return store.getState().scribeRecordModel.tauriInputDevices;
  }, [dispatch.scribeRecordModel, store]);

  // Only the Dictate mic switches, never the session mic. With the separate
  // mic flag off Dictate records from the session mic, so there is nothing to
  // switch.
  const selectDictaphoneInput = useCallback((device: AudioDevice) => {
    setTauriDictationInputDevicePreference(device.name);
  }, []);

  useAutoSelectDictaphoneMic({
    enabled: hardwareMicEnabled && separateMicEnabled,
    refreshInputDevices,
    selectInputDevice: selectDictaphoneInput,
  });

  // Sync SCR-7131 cursor-display widget positioning to Rust.
  useEffect(() => {
    if (!isTauri()) return;

    setWidgetCursorDisplayPositioning(widgetCursorDisplayEnabled).catch(
      (err: unknown) => {
        logger.error({
          event_type: 'DICTATION_WIDGET_CURSOR_DISPLAY_SYNC_FAILED',
          details: { error: err },
        });
      }
    );
  }, [widgetCursorDisplayEnabled]);

  // Sync the SCR-7481 browser-field detection rollout gate to Rust.
  useEffect(() => {
    if (!isTauri()) return;

    setDictationBrowserFieldDetectionEnabled(browserFieldDetectionEnabled).catch(
      (err: unknown) => {
        logger.error({
          event_type: 'DICTATION_BROWSER_FIELD_DETECTION_SYNC_FAILED',
          details: { error: err },
        });
      }
    );
  }, [browserFieldDetectionEnabled]);

  // SCR-6773: pin native Dictate mic from preference (flag on) or session mic
  // (flag off / inherit). Re-runs on flag, preference, or session changes.
  useEffect(() => {
    if (!isTauri()) return;

    const effectivePreference = separateMicEnabled ? dictationPreference : undefined;
    const deviceName = resolveDictationInputDeviceName(
      effectivePreference,
      sessionDeviceName
    );

    void setDictationInputDevice(deviceName).catch((err: unknown) => {
      logger.error({
        event_type: 'DICTATION_INPUT_DEVICE_SYNC_FAILED',
        details: { error: err },
      });
    });
  }, [separateMicEnabled, dictationPreference, sessionDeviceName]);

  // An unplugged Dictate mic falls back to the session mic in Rust.
  useEffect(() => {
    if (!isTauri()) return;

    const fallbackName = resolveDictationInputDeviceName(undefined, sessionDeviceName);
    void setDictationInputDeviceFallback(fallbackName).catch((err: unknown) => {
      logger.error({
        event_type: 'DICTATION_INPUT_DEVICE_FALLBACK_SYNC_FAILED',
        details: { error: sanitizeError(err) },
      });
    });
  }, [sessionDeviceName]);

  // Keep mounted secondary webviews current when either microphone changes.
  useEffect(() => {
    void emitDictationInputDevicePreferenceChanged(
      dictationPreference ?? null,
      sessionDeviceName ?? null
    );
  }, [dictationPreference, sessionDeviceName]);

  // Late-mounted widget controls request a snapshot because Tauri events are
  // not replayed and each webview owns an independent Redux store.
  useEffect(() => {
    if (!isTauri()) return;

    let cancelled = false;
    let unlisten: ManagedUnlistenFn | undefined;

    void (async () => {
      try {
        const un = await managedListen(
          DICTATION_INPUT_DEVICE_PREFERENCE_REQUEST_EVENT,
          () => {
            void emitDictationInputDevicePreferenceChanged(
              dictationPreference ?? null,
              sessionDeviceName ?? null
            );
          }
        );
        if (cancelled) {
          void un();
        } else {
          unlisten = un;
        }
      } catch (err: unknown) {
        logger.error({
          event_type: 'DICTATION_INPUT_DEVICE_PREFERENCE_REQUEST_LISTEN_FAILED',
          details: { error: sanitizeError(err) },
        });
      }
    })();

    return () => {
      cancelled = true;
      // managedListen's release never rejects, so teardown cannot raise an
      // unhandled rejection.
      void unlisten?.();
    };
  }, [dictationPreference, sessionDeviceName]);

  // Main window: apply widget (or other webview) preference broadcasts into
  // the machine-local persisted slice. Native pin is owned by the effect above.
  useEffect(() => {
    if (!isTauri()) return;

    let cancelled = false;
    let unlisten: ManagedUnlistenFn | undefined;

    void (async () => {
      try {
        const un = await managedListen<DictationInputDevicePreferenceChangedPayload>(
          DICTATION_INPUT_DEVICE_PREFERENCE_CHANGED_EVENT,
          (event) => {
            const next = event.payload?.preference;
            // null → inherit (undefined storage); string → explicit preference
            setTauriDictationInputDevicePreference(
              next === null || next === undefined ? undefined : next
            );

            const nextSessionDeviceName = event.payload?.sessionDeviceName;
            if (
              separateMicEnabled === false &&
              nextSessionDeviceName &&
              nextSessionDeviceName !== sessionDeviceName
            ) {
              // Event-time read: subscribing to the device list would re-key
              // this listener on every 10s device poll's fresh array identity.
              const device = store
                .getState()
                .scribeRecordModel.tauriInputDevices?.find(
                  ({ name }) => name === nextSessionDeviceName
                );
              if (device) dispatch.scribeRecordModel.setTauriInputDevice(device);
            }
          }
        );
        if (cancelled) {
          void un();
        } else {
          unlisten = un;
        }
      } catch (err: unknown) {
        logger.error({
          event_type: 'DICTATION_INPUT_DEVICE_PREFERENCE_LISTEN_FAILED',
          details: { error: sanitizeError(err) },
        });
      }
    })();

    return () => {
      cancelled = true;
      // managedListen's release never rejects, so teardown cannot raise an
      // unhandled rejection.
      void unlisten?.();
    };
  }, [dispatch.scribeRecordModel, separateMicEnabled, sessionDeviceName, store]);

  return children;
}
